import React from 'react'

import localStorage from 'helpers/localStorage'

import TopHeader from './TopHeader'
import Sidebar from './Sidebar'
import UserFeedback from 'components/UserFeedback'

type Props = {
    children?: React.ReactNode,
}

const SIDEBAR_LOCAL_STORAGE_KEY = 'SIDEBAR_COMPRESSED'
const DESKTOP_CLASS = 'desktop-sidebar'
const COMPACT_BREAKPOINT = 991

function isCompactViewport () {
    const windowWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0)
    return windowWidth < COMPACT_BREAKPOINT
}

function getInitialSidebarState () {
    const savedState = localStorage.getItem(SIDEBAR_LOCAL_STORAGE_KEY)

    return typeof savedState === 'boolean' ? savedState : isCompactViewport()
}

export default function AppContainer (props: Props) {
    const [isSidebarCompressed, setSidebarCompressed] = React.useState<boolean>(getInitialSidebarState)
    const wasCompactViewport = React.useRef(isCompactViewport())

    const handleCompressSidebar = React.useCallback(() => {
        localStorage.setItem(SIDEBAR_LOCAL_STORAGE_KEY, true)
        setSidebarCompressed(true)
    }, [])

    const handleExpandSidebar = React.useCallback(() => {
        localStorage.setItem(SIDEBAR_LOCAL_STORAGE_KEY, false)
        setSidebarCompressed(false)
    }, [])

    React.useEffect(() => {
        document.body.classList.toggle('__hidden', isSidebarCompressed)
    }, [isSidebarCompressed])

    React.useEffect(() => {
        document.body.classList.add(DESKTOP_CLASS)

        const handleResize = () => {
            const isCompact = isCompactViewport()

            if (isCompact) {
                setSidebarCompressed(true)
            } else if (wasCompactViewport.current) {
                setSidebarCompressed(false)
            }

            wasCompactViewport.current = isCompact
        }

        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    const toggleSidebar = React.useCallback(() => {
        isSidebarCompressed ? handleExpandSidebar() : handleCompressSidebar()
    }, [handleCompressSidebar, handleExpandSidebar, isSidebarCompressed])

    return (
        <>
            {props.children}
            <TopHeader isSidebarOpen={!isSidebarCompressed} toggleSidebar={toggleSidebar}/>
            <Sidebar compressed={isSidebarCompressed} toggleSidebar={toggleSidebar}/>

            {!window.location.hostname.startsWith('auto.app') && (
                <UserFeedback/>
            )}
        </>
    )
}
